
// Pure geometric core for cross-feature line topology - Del linje, Saml
// linjer, Forlæng, Trim, Forbind endpoints, Sæt punkt ved skæring (see
// VectorEditingMenu.js for the interactive wiring, same "compute -> caller
// checks/applies" split as vectorEditing.js). No DOM/THREE dependency -
// plain {x,y,z} points, same convention as vectorEditing.js, Level 1
// (no-browser) unit testable.
//
// Every function here operates on a SINGLE feature's own vertex array
// (mirroring vectorEditing.js's own convention) except where the operation
// is inherently cross-feature (merge/extend/trim/connect/intersect) - those
// take a second, read-only `referencePoints`/`pointsB` array and never
// mutate or return a modified version of it; only the "acted upon" line's
// own array changes. This mirrors the asymmetric "source is acted upon,
// reference stays fixed" convention VectorEditingMenu.js's existing
// runAlignToReference() (Parallel/Perpendicular med reference) already
// established for exactly this reason - consistency, and it means a caller
// only ever needs to commit ONE feature's geometry per operation (Del linje
// is the one exception - see splitPolylineAt()'s own doc comment).
//
//   Linje-topologi (geometry core)
//           |
//           +-- splitPolylineAt()            - Del linje
//           +-- mergePolylines()              - Saml linjer
//           +-- extendPolylineToReference()   - Forlæng
//           +-- trimPolylineToReference()     - Trim (endepunkt-først, reference urørt)
//           +-- trimPolylineAndBridge()       - Trim mod linje (segment/hel linje, indsætter knude i reference + snapper sammen)
//           +-- connectEndpoints()            - Forbind endpoints / Luk gap
//           +-- intersectAndInsert()          - Sæt punkt ved skæring

import {movePoint, insertPointOnSegment, dist3D} from "./vectorEditing.js";
import {
	classifyIntersection3D,
	findPlanarCrossings,
	INTERSECTION_TOLERANCE_M,
	ON_VERTEX_TOLERANCE_M,
	CONNECTION_TOLERANCE_M,
	DEFAULT_PLANAR_TOLERANCE_M,
	DEFAULT_VERTICAL_TOLERANCE_M,
} from "./lineIntersection2D.js";

function clonePoint(p){
	return {x: p.x, y: p.y, z: p.z};
}

// ── Del linje ─────────────────────────────────────────────────────────────
//
// Splits `points` into two independent halves at a position on segment
// `segmentIndex` given by `t` (t=0 -> split exactly at the segment's own
// start vertex, t=1 -> at its end vertex, otherwise a genuinely new point is
// interpolated - same lerp as insertPointOnSegment()). The split position
// becomes the LAST vertex of `first` and the FIRST vertex of `second` - an
// exact shared coordinate, never a near-duplicate, so the two resulting
// features meet with zero gap (task requirement).
//
// Unlike every other function in this file, the caller needs BOTH halves
// (this is the one operation that turns one feature into two) - see
// VectorEditingMenu.js's Del linje handler: `first` replaces the original
// feature's geometry via applyFeatureGeometry(), `second` becomes a whole
// new Feature via DigitizationModule.registerNewFeature().
//
// Refuses (returns null) if either half would end up with fewer than 2
// vertices - a single-point "line" is not a valid split result (same floor
// FeatureVisual.removeVertex()/minVertices already enforces elsewhere).
export function splitPolylineAt(points, segmentIndex, t){
	const clampedT = Math.max(0, Math.min(1, t));
	const A = points[segmentIndex], B = points[segmentIndex + 1];
	const splitPoint = clampedT <= 0 ? clonePoint(A) : clampedT >= 1 ? clonePoint(B) : {
		x: A.x + clampedT * (B.x - A.x),
		y: A.y + clampedT * (B.y - A.y),
		z: A.z + clampedT * (B.z - A.z),
	};

	const firstInterior = points.slice(0, segmentIndex + 1).map(clonePoint);
	const secondInterior = points.slice(segmentIndex + 1).map(clonePoint);

	// avoid duplicating the split point when it exactly coincides with an
	// existing vertex (t essentially 0 or 1) - each half still ends/starts
	// with splitPoint exactly once
	const first = clampedT > 0 ? [...firstInterior, splitPoint] : firstInterior;
	const second = clampedT < 1 ? [splitPoint, ...secondInterior] : secondInterior;

	if(first.length < 2 || second.length < 2) return null;

	return {first, second, splitPoint};
}

// ── Saml linjer ───────────────────────────────────────────────────────────
//
// Tries all 4 endpoint pairings (A-start/B-start, A-start/B-end, A-end/
// B-start, A-end/B-end), picks whichever pair is closest together (3D -
// task section 3 applies here too: two lines whose endpoints are close in
// XY but far apart in Z should not be silently joined), reverses `pointsB`
// when needed so the result reads head-to-tail in a single consistent
// direction (task requirement: "håndtere linjernes retning korrekt"), and
// drops ONE copy of the shared vertex when the winning pair is (near-)
// exactly coincident so the joined line never carries an unintended
// duplicate vertex at the seam (task requirement).
//
// Refuses ({error}) when even the closest pairing exceeds `toleranceGap` -
// merge requires the lines to actually meet; a real gap should be closed
// first (connectEndpoints()) or the caller should use Forbind endpoints
// instead of Saml, not have this function silently bridge an arbitrary gap.
export function mergePolylines(pointsA, pointsB, {toleranceGap = CONNECTION_TOLERANCE_M} = {}){
	const aStart = pointsA[0], aEnd = pointsA[pointsA.length - 1];
	const bStart = pointsB[0], bEnd = pointsB[pointsB.length - 1];

	// four candidate pairings: [gap, endOfA-is-aEnd, startOfB-is-bStart]
	// expressed as which end of each line becomes the shared seam
	const candidates = [
		{gap: dist3D(aEnd, bStart), reverseA: false, reverseB: false},
		{gap: dist3D(aEnd, bEnd), reverseA: false, reverseB: true},
		{gap: dist3D(aStart, bStart), reverseA: true, reverseB: false},
		{gap: dist3D(aStart, bEnd), reverseA: true, reverseB: true},
	];

	let best = candidates[0];
	for(const c of candidates) if(c.gap < best.gap) best = c;

	if(best.gap > toleranceGap){
		return {error: "no-shared-endpoint", closestGap: best.gap};
	}

	const orderedA = (best.reverseA ? pointsA.slice().reverse() : pointsA).map(clonePoint);
	const orderedB = (best.reverseB ? pointsB.slice().reverse() : pointsB).map(clonePoint);

	// orderedA's last vertex and orderedB's first vertex are now the seam -
	// drop orderedB's own copy when they're (near-)exactly the same point,
	// otherwise keep both (the small real gap becomes one short final
	// segment, never silently discarded/moved without the caller asking)
	const seamGap = dist3D(orderedA[orderedA.length - 1], orderedB[0]);
	const joined = seamGap <= 1e-9 ? [...orderedA, ...orderedB.slice(1)] : [...orderedA, ...orderedB];

	return {points: joined, seamGap};
}

// ── Forlæng til reference (XY first, §7) ──────────────────────────────────
//
// Extends the line's own endpoint (`endpointIndex`, 0 or the last index)
// OUTWARD in the XY plane along the direction of its own terminal segment
// until it meets `referencePoints` - a ray (tRange [0, Infinity)) against the
// reference's real bounded segments, never the reference's infinite-line
// extension.
//
// New endpoint:
//   X/Y = the XY crossing.
//   Z   = LINEAR EXTRAPOLATION of the source's own terminal segment (the ray
//         carries that slope, so `hit.zA` at the crossing is exactly that).
//         The reference line's elevation is NEVER adopted (§1.4 / §7).
//
// A height difference between source and reference at the crossing does not
// block the extend - it is a plan operation. Only the endpoint moves; no new
// vertex is added. Refuses ({error}) only when the line has < 2 points or the
// ray meets the reference nowhere.
export function extendPolylineToReference(points, endpointIndex, referencePoints, options = {}){
	if(points.length < 2) return {error: "too-few-points"};
	const isEnd = endpointIndex === points.length - 1;
	if(endpointIndex !== 0 && !isEnd) return {error: "not-an-endpoint"};

	const endpoint = points[endpointIndex];
	const neighbour = points[isEnd ? endpointIndex - 1 : endpointIndex + 1];
	// ray origin = endpoint, direction = away from its own neighbour, carrying
	// the terminal segment's own 3D slope so hit.zA IS the source extrapolation
	const rayFar = {
		x: endpoint.x + (endpoint.x - neighbour.x),
		y: endpoint.y + (endpoint.y - neighbour.y),
		z: endpoint.z + (endpoint.z - neighbour.z),
	};

	let best = null;
	for(let j = 0; j < referencePoints.length - 1; j++){
		const hit = classifyIntersection3D(endpoint, rayFar, referencePoints[j], referencePoints[j + 1], {
			planarTolerance: options.planarTolerance ?? INTERSECTION_TOLERANCE_M,
			verticalTolerance: Infinity, // plan operation - a Z mismatch never rejects the crossing
			tRange: [0, Infinity],
			sRange: [0, 1],
		});
		if(hit.kind !== "none" && (!best || hit.t < best.t)) best = hit;
	}

	if(!best) return {error: "no-intersection"};

	const result = points.map(clonePoint);
	// X/Y from the crossing; Z extrapolated from the source's terminal segment
	result[endpointIndex] = {x: best.x, y: best.y, z: best.zA};
	return {points: result, hit: best};
}

// ── Trim til reference (endpoint-first, §6) ───────────────────────────────
//
// Shortens the line back to the nearest XY crossing with `referencePoints`,
// walking inward from `endpointIndex`. SAME intersection model as Trim mod
// linje (findPlanarCrossings - XY only, a height difference does not block
// it); the only difference from that tool is the workflow (this one starts
// from a picked endpoint, and it never touches the reference). Unlike
// Forlæng it never extrapolates - the two lines must already cross in plan.
//
// New terminal vertex:
//   X/Y = the XY crossing.
//   Z   = the SOURCE line's own elevation at the crossing (`hit.zA`) - never
//         the reference's (§1.4 / §6).
//
// Truncates every vertex beyond the chosen crossing; if the kept side's own
// terminal is already at the crossing (within ON_VERTEX_TOLERANCE_M in XY) it
// is snapped there rather than a near-zero-length segment appended. Refuses
// if the result would drop below 2 vertices.
export function trimPolylineToReference(points, endpointIndex, referencePoints, options = {}){
	if(points.length < 2) return {error: "too-few-points"};
	const isEnd = endpointIndex === points.length - 1;
	if(endpointIndex !== 0 && !isEnd) return {error: "not-an-endpoint"};

	const planarTol = options.planarTolerance ?? INTERSECTION_TOLERANCE_M;
	const crossings = findPlanarCrossings(points, referencePoints, {planarTolerance: planarTol});
	if(crossings.length === 0) return {error: "no-intersection"};

	// walking inward from the chosen endpoint: nearest by segment index,
	// then by t along that segment
	const nearest = crossings.reduce((best, h) => {
		const hSteps = isEnd ? (points.length - 2 - h.segIndexA) : h.segIndexA;
		const hKey = hSteps + (isEnd ? 1 - h.t : h.t);
		const bestSteps = isEnd ? (points.length - 2 - best.segIndexA) : best.segIndexA;
		const bestKey = bestSteps + (isEnd ? 1 - best.t : best.t);
		return hKey < bestKey ? h : best;
	});

	// X/Y from the crossing; Z from the SOURCE line (`points`) at the crossing
	const crossing = {x: nearest.x, y: nearest.y, z: nearest.zA};
	const coincidentXY = (p) => Math.hypot(p.x - crossing.x, p.y - crossing.y) <= ON_VERTEX_TOLERANCE_M;
	let kept;
	if(isEnd){
		kept = points.slice(0, nearest.segIndexA + 1).map(clonePoint);
		if(coincidentXY(kept[kept.length - 1])) kept[kept.length - 1] = clonePoint(crossing);
		else kept.push(crossing);
	}else{
		kept = points.slice(nearest.segIndexA + 1).map(clonePoint);
		if(coincidentXY(kept[0])) kept[0] = clonePoint(crossing);
		else kept.unshift(crossing);
	}

	if(kept.length < 2) return {error: "result-too-short"};

	return {points: kept, hit: nearest};
}

// ── Trim mod linje (segment/hel linje -> reference, med fælles knude) ──────
//
// The interactive counterpart to trimPolylineToReference() above, but driven
// by the CURRENT selection rather than a pre-picked endpoint, and - unlike
// every other function in this file - it DOES return a modified copy of the
// reference line: a real vertex is inserted into `referencePoints` at the
// crossing, and `points`' cut end is snapped to that exact same coordinate,
// so the two lines end up sharing one bit-identical vertex (topology
// preserved / "snappet sammen", task requirement).
//
// Which part of `points` is kept:
//   - `keepSegmentIndex` given (a single segment was selected): the side of
//     the nearest real crossing that CONTAINS that segment is kept; the rest
//     is discarded. If the crossing happens to land on the selected segment
//     itself (no unambiguous "kept side" from the selection alone), the
//     longer of the two sides is kept.
//   - `keepSegmentIndex === null` (the whole line was selected): the end with
//     the SHORTEST overshoot past the reference (across every real crossing)
//     is trimmed off; everything else is kept.
//
// "Trim mod linje" is a TOP-VIEW (XY / plan) operation (§1.3 / §5): it trims
// the line where it crosses the reference IN PLAN, regardless of any height
// difference between the two lines there.
//
// Z rule (§1.4): the two new/reused vertices share the SAME X/Y (the XY
// crossing) but each takes the elevation of ITS OWN feature:
//   sourceVertex    = {crossing XY, source line's Z there}   -> trim line's cut end
//   referenceVertex = {crossing XY, reference line's Z there} -> inserted into,
//                      or reused from, the reference
// Reference-Z is NEVER copied onto the source.
//
// The reference vertex is inserted ON the reference's own segment (so the
// reference keeps its shape, only gaining a vertex). If the crossing lands on
// one of that segment's own end vertices (within ON_VERTEX_TOLERANCE_M in XY)
// the existing vertex is reused and nothing is inserted
// (`insertedIntoReference: false`).
//
// Returns {trimmedPoints, referencePoints, insertedIntoReference,
// sourceVertex, referenceVertex, cutEnd:"start"|"end"} on success, or {error}
// ("too-few-points" | "no-intersection" | "result-too-short").
export function trimPolylineAndBridge(points, referencePoints, options = {}){
	const {keepSegmentIndex = null} = options;
	if(points.length < 2 || referencePoints.length < 2) return {error: "too-few-points"};

	const intersectionTol = options.planarTolerance ?? INTERSECTION_TOLERANCE_M;
	const distXY = (p, q) => Math.hypot(q.x - p.x, q.y - p.y);
	const atParamEnd = (param, segLenXY, tolM) => {
		if(!(segLenXY > 1e-9)) return true;
		const tol = tolM / segLenXY;
		const c = Math.max(0, Math.min(1, param));
		return c <= tol || c >= 1 - tol;
	};

	// XY-only crossing test (a height difference between the lines never
	// rejects an in-plan crossing here - see findPlanarCrossings).
	let crossings = findPlanarCrossings(points, referencePoints, {planarTolerance: intersectionTol});
	if(crossings.length === 0) return {error: "no-intersection"};

	// cumulative 3D arc length up to each vertex of `points` - a segment is
	// straight, so `hit.t` (an XY parameter) is also its exact fraction of
	// that segment's 3D length
	const cum = [0];
	for(let i = 1; i < points.length; i++) cum.push(cum[i - 1] + dist3D(points[i - 1], points[i]));
	const totalLen = cum[points.length - 1];
	const arcLenToHit = (h) => cum[h.segIndexA] + h.t * (cum[h.segIndexA + 1] - cum[h.segIndexA]);

	// The filters below are all NON-DESTRUCTIVE: each narrows the candidate
	// set only if something is left afterwards. A single crossing is always
	// good enough to act on - refusing an obvious, visible crossing because a
	// heuristic disliked it is exactly the "not usable" behaviour being fixed.

	// (a) Prefer crossings where the trim line genuinely overshoots the
	// reference, over one where it already ends on (within tolerance of) the
	// reference - trimming to the latter removes nothing. Typical trigger: the
	// trim line's far end was previously snapped onto this same reference.
	const overshoots = crossings.filter((h) => {
		const a = arcLenToHit(h);
		return Math.min(a, totalLen - a) > intersectionTol;
	});
	if(overshoots.length > 0) crossings = overshoots;

	// (b) Prefer a genuine transversal crossing over one that merely sits on a
	// vertex of BOTH lines (where the lines are joined, not crossing).
	const refSegLenXY = (h) => distXY(referencePoints[h.segIndexB], referencePoints[h.segIndexB + 1]);
	const trimSegLenXY = (h) => distXY(points[h.segIndexA], points[h.segIndexA + 1]);
	const isJoin = (h) => atParamEnd(h.s, refSegLenXY(h), ON_VERTEX_TOLERANCE_M) && atParamEnd(h.t, trimSegLenXY(h), ON_VERTEX_TOLERANCE_M);
	const transversal = crossings.filter((h) => !isJoin(h));
	const pool = transversal.length > 0 ? transversal : crossings;

	// choose the crossing to trim to, and which end of `points` gets cut
	let hit, cutEnd;
	if(keepSegmentIndex === null){
		let bestOvershoot = Infinity;
		for(const h of pool){
			const startLen = arcLenToHit(h);
			const endLen = totalLen - startLen;
			const overshoot = Math.min(startLen, endLen);
			if(overshoot < bestOvershoot){
				bestOvershoot = overshoot;
				hit = h;
				cutEnd = startLen <= endLen ? "start" : "end";
			}
		}
	}else{
		const target = keepSegmentIndex + 0.5; // midpoint of the selected segment, in polyline-parameter space
		hit = pool.reduce((best, h) =>
			Math.abs((h.segIndexA + h.t) - target) < Math.abs((best.segIndexA + best.t) - target) ? h : best);
		const c = hit.segIndexA + hit.t;
		if(c >= keepSegmentIndex + 1){
			cutEnd = "end"; // crossing lies beyond the selected segment -> discard the tail
		}else if(c <= keepSegmentIndex){
			cutEnd = "start"; // crossing lies before the selected segment -> discard the head
		}else{
			// crossing lands on the selected segment itself - keep the longer side
			const startLen = arcLenToHit(hit);
			cutEnd = startLen >= totalLen - startLen ? "end" : "start";
		}
	}

	// (c) The REFERENCE vertex: XY = the crossing, Z = the REFERENCE line's own
	// elevation there (hit.zB). `hit.s` can sit a hair outside [0,1] via the
	// bounds slack - clamp first; if it then lands on the reference segment's
	// own start/end vertex (within ON_VERTEX_TOLERANCE_M in XY) reuse that
	// existing vertex, otherwise insert one. insertPointOnSegment(ref, j, s)
	// yields exactly {crossing XY, hit.zB} by construction.
	const rb0 = referencePoints[hit.segIndexB], rb1 = referencePoints[hit.segIndexB + 1];
	const refLenXY = distXY(rb0, rb1);
	const sTol = refLenXY > 1e-9 ? ON_VERTEX_TOLERANCE_M / refLenXY : Infinity;
	const s = Math.max(0, Math.min(1, hit.s));

	let refOut, referenceVertex, insertedIntoReference;
	if(s <= sTol){
		refOut = referencePoints.map(clonePoint);
		referenceVertex = clonePoint(rb0);
		insertedIntoReference = false;
	}else if(s >= 1 - sTol){
		refOut = referencePoints.map(clonePoint);
		referenceVertex = clonePoint(rb1);
		insertedIntoReference = false;
	}else{
		refOut = insertPointOnSegment(referencePoints, hit.segIndexB, s);
		referenceVertex = clonePoint(refOut[hit.segIndexB + 1]);
		insertedIntoReference = true;
	}

	// The SOURCE (trim line) cut vertex: SAME X/Y as the reference vertex, but
	// the SOURCE line's OWN elevation at the crossing (hit.zA). Reference-Z is
	// never copied here (§1.4).
	const sourceVertex = {x: referenceVertex.x, y: referenceVertex.y, z: hit.zA};

	// `points`' trimmed vertex array: keep the chosen side, terminated at
	// sourceVertex. If the kept side's own terminal is already at the crossing
	// (within ON_VERTEX_TOLERANCE_M in XY) snap it there instead of appending a
	// near-zero-length final segment.
	let kept;
	if(cutEnd === "end"){
		kept = points.slice(0, hit.segIndexA + 1).map(clonePoint);
		if(distXY(kept[kept.length - 1], sourceVertex) <= ON_VERTEX_TOLERANCE_M) kept[kept.length - 1] = clonePoint(sourceVertex);
		else kept.push(clonePoint(sourceVertex));
	}else{
		kept = points.slice(hit.segIndexA + 1).map(clonePoint);
		if(distXY(kept[0], sourceVertex) <= ON_VERTEX_TOLERANCE_M) kept[0] = clonePoint(sourceVertex);
		else kept.unshift(clonePoint(sourceVertex));
	}

	if(kept.length < 2) return {error: "result-too-short"};

	return {trimmedPoints: kept, referencePoints: refOut, insertedIntoReference, sourceVertex, referenceVertex, cutEnd};
}

// ── Forbind endpoints / Luk gap ─────────────────────────────────────────────
//
// Moves endpoint `indexA` of `pointsA` exactly onto endpoint `indexB` of
// `pointsB` (`pointsB` itself is never modified - see this file's own doc
// comment on the asymmetric source/reference convention). Deliberately just
// a thin wrapper around vectorEditing.js's own movePoint() - "replace one
// vertex's position outright" is already exactly what that function does;
// this file does not need its own second implementation of it (task section
// 7 - reuse existing robust functions).
//
// Used both by the manual "Forbind til andet endpoint..." vertex-menu
// action AND by the Topology-QA panel's "Luk gap" quick-action on a
// detected near-miss (geometry/topologyQa.js's findNearbyEndpointGaps) -
// same underlying operation, two entry points, not two implementations.
export function connectEndpoints(pointsA, indexA, pointsB, indexB){
	return movePoint(pointsA, indexA, pointsB[indexB]);
}

// ── Sæt punkt ved skæring (§8) ───────────────────────────────────────────
//
// Same XY crossing model as Trim (findPlanarCrossings - a height difference
// between the lines does not block it). Inserts exactly ONE new vertex into
// `points` at the crossing; if more than one crossing exists, picks whichever
// is closest to `near` (the user's click). `referencePoints` is NEVER
// modified as a side effect - to also split the other line the caller runs
// this again from that line.
//
//   X/Y = the crossing.
//   Z   = interpolated along the SOURCE segment the vertex is inserted on
//         (insertPointOnSegment lerps the source's own Z at `chosen.t`) - so
//         the new vertex takes the source feature's elevation, never the
//         reference's (§1.4 / §8).
export function intersectAndInsert(points, referencePoints, near, options = {}){
	const crossings = findPlanarCrossings(points, referencePoints, {
		planarTolerance: options.planarTolerance ?? INTERSECTION_TOLERANCE_M,
	});
	if(crossings.length === 0) return {error: "no-intersection"};

	const distanceToNear = (h) => near ? Math.hypot(h.x - near.x, h.y - near.y) : 0;
	const chosen = crossings.reduce((best, h) => distanceToNear(h) < distanceToNear(best) ? h : best);

	const result = insertPointOnSegment(points, chosen.segIndexA, chosen.t);
	return {points: result, insertedAt: {x: chosen.x, y: chosen.y, z: chosen.zA}};
}

export {DEFAULT_PLANAR_TOLERANCE_M, DEFAULT_VERTICAL_TOLERANCE_M, INTERSECTION_TOLERANCE_M, ON_VERTEX_TOLERANCE_M, CONNECTION_TOLERANCE_M};
